
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Bot } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface AskJudiAnalyzerProps {
  onBioGenerated: (bio: string) => void;
  onBioChange: (bio: string) => void;
  formData: any;
  photos: string[];
  publicInterests: string[];
  bio: string;
}

// Helper function to calculate age from date of birth
const calculateAge = (dateOfBirth: string): number => {
  if (!dateOfBirth) return 0;
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const AskJudiAnalyzer = ({
  onBioGenerated,
  onBioChange,
  formData,
  photos,
  publicInterests,
  bio
}: AskJudiAnalyzerProps) => {
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const generateProfile = async () => {
    if (!consent) {
      toast({
        title: "Consent Required",
        description: "Please agree to the AI analysis terms.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const age = calculateAge(formData.date_of_birth);
      
      const profileData = {
        name: formData.display_name || "User",
        age: age || "Unknown",
        height: formData.height || "Unknown",
        weight: formData.weight || "Unknown",
        location: formData.address || "Unknown",
        bio: bio || "",
        hashtags: publicInterests,
        imageCount: photos.length,
        personality: "passive_assertive" // Default personality
      };

      const {
        data,
        error
      } = await supabase.functions.invoke('ask-judi-profile-analysis', {
        body: profileData
      });

      if (error) throw error;

      if (data?.bio) {
        onBioGenerated(data.bio);
        onBioChange(data.bio);
      }

      toast({
        title: "Judi Has Spoken! ✨",
        description: "Your AI-generated bio suggestion is ready! Feel free to edit it."
      });
    } catch (error: any) {
      console.error('Ask Judi analysis failed:', error);
      toast({
        title: "Judi is Taking a Break",
        description: error.message || "AI analysis failed. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mb-6 bg-carbon border-ice-white/10">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-center my-0 py-0 px-[26px]">
          <Bot className="w-5 h-5 text-electric-violet" />
          Ask Judi - Real AI Profile Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div>
            <Label htmlFor="bio-textarea" className="text-ice-white font-medium mb-2 block">
              About Me (Judi's Suggestions)
            </Label>
            <Textarea
              id="bio-textarea"
              placeholder="Judi will generate a creative bio for you here. You can edit it as needed!"
              value={bio}
              onChange={(e) => onBioChange(e.target.value)}
              className="min-h-[120px] bg-carbon border-ice-white/20 text-ice-white placeholder:text-ice-white/50 focus:border-electric-violet"
            />
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox 
            id="consent" 
            checked={consent} 
            onCheckedChange={(checked) => setConsent(checked as boolean)} 
          />
          <label htmlFor="consent" className="text-sm text-ice-white/80">
            I consent to AI analysis of my profile data for content generation
          </label>
        </div>

        <Button 
          onClick={generateProfile} 
          disabled={loading || !consent} 
          className="w-full bg-electric-violet hover:bg-electric-violet/80 text-ice-white font-medium"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Judi is Analyzing...
            </>
          ) : (
            <>
              <Bot className="w-4 h-4 mr-2" />
              Ask Judi to Generate Profile
            </>
          )}
        </Button>

        <div className="text-xs text-ice-white/60 mt-2">
          Judi uses OpenAI's GPT-4o-mini to create funny, juicy, creative, and sophisticated profiles.
          All suggestions are editable - make them your own!
        </div>
      </CardContent>
    </Card>
  );
};
